import React, { createContext, useContext, useState, useEffect } from "react";
import ApiService from "./ApiService";

const CityContext = createContext();

export const useCity = () => useContext(CityContext);

export const CityProvider = ({ children }) => {
  const [selectedCity, setSelectedCity] = useState(localStorage.getItem("city") || "Cities");
  const [selectedCityTagline, setSelectedCityTagline] = useState(localStorage.getItem("cityTagline") || "");
  const [cities, setCities] = useState([]);
  const { getList } = ApiService();

  const cityTaglines = {
    "Kolkata": "Cultural capital",
    "Howrah": "Spiritual and riverside art",
    "Durgapur": "Steel City with cultural soul",
    "Asansol": "Industrial and festive hub",
    "Siliguri": "Gateway with folk energy",
    "Maheshtala": "Suburban riverside life",
    "Rajpur Sonarpur": "Southern Kolkata suburb",
    "Bardhaman": "Historical and melodic heritage",
    "Murshidabad": "Royal city of Nawabs",
    "Habra": "Northern suburban town",
    "Kharagpur": "Academic and campus-culture town",
    "Santipur": "Handloom weaving capital",
    "English Bazar/Malda": "Riverine and craft-market city",
    "Chandannagar": "French-colonial riverside gem",
    "Bally": "Hooghly-bank industrial town",
    "Patna": "Ancient heart of Bihar",
    "Ranchi": "Land of waterfalls and forests",
    "Bhubaneswar": "The Temple City of India",
    "Cuttack": "Silver City and Cultural capital",
    "Gaya": "Spiritual enlightenment hub",
    "Nadia": "Spiritual hub and ISKCON",
    "Hooghly": "Historic riverside heritage"
  };

  const cityDetails = {
    "Kolkata": {
      architecture: "Colonial-era Victoria Memorial, Indian Museum, St Paul's Cathedral, and St John's Church; art galleries such as the Academy of Fine Arts and Birla Academy of Art & Culture; the Kumartuli clay-idol-making quarter.",
      culture: "Home of Durga Puja with elaborate pandals, art installations, and neighbourhood-club competitions; vibrant theatre, Rabindra Sangeet, and a café-literature scene centred around Park Street and College Street."
    },
    "Howrah": {
      architecture: "The Ramakrishna Mission's Belur Math, a syncretic temple complex blending Hindu, Islamic, and Christian styles, with intricate carvings and an open-air prayer ground.",
      culture: "Major centre for Ramakrishna-Vivekananda-related events, including birthday celebrations and discourses; the ghats and riverfront often host local music and devotional gatherings."
    },
    "Durgapur": {
      traditions: "Local community clubs create elaborate Durga, Kali, and Jagaddhatri puja pandals using modern and traditional art, often with light shows and installations.",
      culture: "Poila Boishakh (Bengali New Year), Holi/Basant Utsav, and Kali Puja are big, with music, dance-drama, and street-food festivals; lots of group-singing and amateur theatre."
    },
    "Asansol": {
      traditions: "Local puja committees hire professional artists for pandal themes, murals, and eco-friendly idols; community-led cultural evenings during Durga, Kali, and Jagaddhatri Pujas.",
      culture: "Durga Puja dominates, with neighbourhood pandals competing on design; Basant Utsav (Holi-as-spring festival) and Poila Boishakh bring songs, street theatre, and traditional music."
    },
    "Siliguri": {
      performances: "Local stages and cultural centres host folk-inspired theatre, dance, and storytelling; the city absorbs influences from Darjeeling, Sikkim, and plains-Bengal.",
      culture: "Celebrates Durga Puja and local seasonal festivals; community-based art-craft shows and music events reflect the mix of Bengali, Nepali, and tribal cultures."
    },
    "Maheshtala": {
      culture: "Smaller riverside temples and neighbourhood puja pandals where local residents commission traditional idols and decorate them communally.",
      festivals: "Participates in Greater-Kolkata-style Durga Puja and Kali Puja with modest but lively pandals and food stalls."
    },
    "Rajpur Sonarpur": {
      culture: "Decent local puja clubs and community centres that host small-scale music, dance, and theatre; more \"lived-in\" cultural vibe than big-city showpiece.",
      festivals: "Local Durga Puja celebrations with community-organised pandals and night-time cultural programmes."
    },
    "Bardhaman": {
      heritage: "Historic Burdwan Raj palace and temples; centres for classical music and traditional Bengali folk; the Meghnad Saha Planetarium also hosts cultural events.",
      festivals: "Rath Yatra, Durga Puja, and Kali Puja include temple-based processions, local music, and rural-style folk arts in the surrounding villages."
    },
    "Murshidabad": {
      heritage: "Hazarduari Palace draws visitors with its Indo-Persian-European architecture and royal collections; the city's old streets host traditional music and folk performances.",
      culture: "Street musicians and folk singers perform during festivals; local food festivals showcase the sweets and savouries of the region, blending Bengali-Muslim traditions."
    },
    "Habra": {
      culture: "Local temples and community centres with modest mural-style decorations; Durga Puja clubs design simple but colourful pandals.",
      festivals: "Local Durga Puja and Kali Puja are the main cultural events, with community-sponsored music and dance programmes."
    },
    "Kharagpur": {
      culture: "IIT-Kharagpur and nearby colleges host art exhibitions, theatre, and music festivals; the town has a strong student-art vibe.",
      festivals: "Campus-centred Durga Puja and Kali Puja, with student-designed pandals and cultural nights; classical music and drama groups from the institutions."
    },
    "Shantipur": {
      craft: "Famous for Shantipuri tant-saris and handloom weaving; the streets are lined with looms and small family workshops where you can watch artisans weave in real time.",
      culture: "The town's identity is deeply tied to its weavers; local festivals often include recognition of master weavers and traditional songs sung at the looms."
    },
    "English Bazar": {
      heritage: "Museums and heritage sites linked to the medieval Bengal-Sultanate era; markets sell traditional handicrafts, textiles, and local artwork.",
      culture: "Local fairs and cultural shows display folk music, dance, and traditional crafts; markets are a living museum of everyday Bengali-Muslim art and design."
    },
    "Chandannagar": {
      architecture: "French-style buildings, promenade (Strand), and colonial churches; the old town feels like a blend of European and Bengali aesthetics.",
      culture: "Carnival-style events and local festivals preserve a mix of French-Bengali traditions; food stalls offer Franco-Bengali sweets and snacks along the river."
    },
    "Bally": {
      culture: "Local temples and small cultural centres; modest Durga Puja and Kali Puja pandals with community-supported art and music shows.",
      festivals: "Participates in the broader Howrah-Kolkata circuit of Durga Puja, with street-lighting and neighbourhood-organised events."
    },
    "Nadia": {
      spirituality: "Home to ISKCON Mayapur, the spiritual headquarters of the Hare Krishna movement; famous for its grand temples and peaceful atmosphere.",
      culture: "Vibrant festivals and devotional music (kirtan) that attract visitors from all over the world."
    },
    "Hooghly": {
      heritage: "Rich with colonial history, including Portuguese, Dutch, and British influences; famous for its historic churches and riverside architecture.",
      culture: "Traditional Bengali culture blended with historic European influences."
    }
  };

  useEffect(() => {
    const fetchCities = async () => {
      try {
        let dataInfo = await getList("city-list", {});
        if (dataInfo.responseCode === 200) {
          const allowedCities = [
            "Kolkata",
            "Howrah",
            "Bally",
            "Rajpur Sonarpur",
            "Maheshtala",
            "Habra",
            "Chandannagar",
            "Nadia",
            "Santipur",
            "Hooghly",
            "Durgapur",
            "Bardhaman",
            "Asansol",
            "Kharagpur",
            "English Bazar/Malda",
            "Murshidabad",
            "Siliguri",
            "Ranchi",
            "Bhubaneswar",
            "Cuttack",
            "Patna",
            "Gaya"
          ];

          // Merge API data with allowed cities to ensure all show up
          const finalCities = allowedCities.map(cityName => {
            const apiCity = dataInfo.responseData.find(c => c.name === cityName);
            if (apiCity) {
              return {
                ...apiCity,
                tagline: cityTaglines[cityName] || ""
              };
            } else {
              // Fallback for cities not in API yet
              return {
                id: cityName.toLowerCase().replace(/\s+/g, '-'),
                name: cityName,
                tagline: cityTaglines[cityName] || ""
              };
            }
          });
          
          setCities(finalCities);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  return (
    <CityContext.Provider
      value={{
        selectedCity,
        setSelectedCity,
        selectedCityTagline,
        setSelectedCityTagline,
        cities,
        cityTaglines,
        cityDetails
      }}
    >
      {children}
    </CityContext.Provider>
  );
};
